import React from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Calendar as CalIcon, Receipt, TrendingDown, Sun, Moon, LogOut, FlaskConical, Settings as SettingsIcon, Wallet, ShieldCheck, BookOpen } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import SeasonalEffect from "@/components/SeasonalEffect";
import NotificationBell from "@/components/NotificationBell";
import ShareCTA from "@/components/ShareCTA";
import FeedbackButton from "@/components/FeedbackButton";
import TopBanners from "@/components/TopBanners";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/calendar", label: "Calendar", icon: CalIcon, testid: "nav-calendar" },
  { to: "/bills", label: "Bills", icon: Receipt, testid: "nav-bills" },
  { to: "/debts", label: "Debts", icon: TrendingDown, testid: "nav-debts" },
  { to: "/cashflow", label: "Cash Flow", icon: Wallet, testid: "nav-cashflow" },
  { to: "/simulator", label: "Simulator", icon: FlaskConical, testid: "nav-simulator" },
  { to: "/guide", label: "Guide", icon: BookOpen, testid: "nav-guide" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { resolved, toggle } = useTheme();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <SeasonalEffect />
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/60 backdrop-blur-md p-4 sticky top-0 h-screen" data-testid="sidebar">
        <div className="flex items-center gap-2 mb-8 px-2">
          <BrandLogo className="h-9 w-9 rounded-xl shadow-md" testid="brand-logo" />
          <div>
            <div className="font-display font-bold text-lg leading-none">OWEverit</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Every balance, gone</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {links.map(l => (
            <NavLink
              key={l.to} to={l.to} data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-teal-500/10 text-teal-600 dark:text-teal-300 border border-teal-500/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                }`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink to="/admin" data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border border-indigo-500/20" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                }`}>
              <ShieldCheck className="h-4 w-4" /> Admin
            </NavLink>
          )}
        </nav>

        <div className="mt-auto flex flex-col gap-2">
          <div className="mb-2"><NotificationBell /></div>
          <ShareCTA />
          <FeedbackButton />
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-accent/40">
            <div className="flex items-center gap-2 min-w-0">
              {user?.picture ? (
                <img src={user.picture} className="h-8 w-8 rounded-full object-cover border border-border" alt="" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-teal-400 to-indigo-500" />
              )}
              <div className="min-w-0">
                <div className="text-xs font-semibold truncate">{user?.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
              </div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="text-muted-foreground hover:text-rose-500 transition">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 border-b border-border bg-card/80 backdrop-blur-md flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <BrandLogo className="h-8 w-8 rounded-lg" />
          <span className="font-display font-bold">OWEverit</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell compact />
          <button onClick={toggle} className="p-2 rounded-lg border border-border">
            {resolved === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0">
        <div className="md:hidden h-14" />
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto fade-up">
          <TopBanners />
          <VerifyEmailBanner />
          {children}
        </div>
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card/90 backdrop-blur-md grid grid-cols-8">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} className={({isActive}) =>
              `flex flex-col items-center gap-1 py-2 text-[10px] ${isActive ? "text-teal-500" : "text-muted-foreground"}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="md:hidden h-16" />
      </main>
    </div>
  );
}
